import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import { ReactNode } from "react";
import { useTranslate } from "../i18n";

interface ConfirmDialogProps {
    visible: boolean;
    /** `danger` for a permanent delete, `warning` for a reversible detach. */
    tone?: "danger" | "warning";
    title: ReactNode;
    body: ReactNode;
    confirmLabel: string;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * The confirmation shown before a delete or a removal from the clinic.
 *
 * These used to be untitled sentences with two equally weighted Yes/No buttons,
 * which is thin ceremony for an irreversible delete. This one is titled and
 * marked, and its confirm button carries the weight of what it does while
 * Cancel stays the default.
 */
export const ConfirmDialog = ({
    visible,
    tone = "danger",
    title,
    body,
    confirmLabel,
    onConfirm,
    onCancel
}: ConfirmDialogProps) => {
    const t = useTranslate();

    return (
        <Modal
            visible={visible}
            okText={confirmLabel}
            cancelText={t("common.cancel")}
            okButtonProps={{ danger: tone === "danger" }}
            onCancel={onCancel}
            onOk={onConfirm}
            width={460}
            closable={false}
        >
            <div className="confirm">
                <span className={`confirm__mark${tone === "warning" ? " confirm__mark--warn" : ""}`}>
                    <ExclamationCircleOutlined />
                </span>
                <div>
                    <div className="confirm__title">{title}</div>
                    <div className="confirm__body">{body}</div>
                </div>
            </div>
        </Modal>
    );
};
